import { ReactElement, useMemo } from 'react';

import { UploadResult } from 'firebase/storage';

import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import LanguageIcon from '@mui/icons-material/Language';

import Org, { ORG_NOTIFICATION_TYPES, OrgNotificationType } from '../../types/Org';
import GratiImageUpload from '../common/GratiImageUpload';

import OrgPublicPageEditor from './OrgPublicPageEditor';

interface OrgGeneralSettingsEditorProps {
  org: Org;
  isNew?: boolean;
  onUpdate: <K extends keyof Org>(field: K, value: Org[K]) => void;
  onImageUpdated?: (snapshot: UploadResult | null) => void;
  onFileSelected?: (file: File | null) => void;
  onRequestVanityUrl?: (vanityUrl: string) => Promise<void>;
  onCancelVanityUrl?: () => Promise<void>;
}

// Validate URL format - requires proper domain with TLD (e.g., example.com)
const isValidUrl = (url: string): boolean => {
  if (!url) return true; // Empty is valid (field is optional)
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
    const isValidProtocol = urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    // Require hostname to have at least one dot (e.g., example.com, not just "pickle")
    const hasValidHostname = urlObj.hostname.includes('.');
    return isValidProtocol && hasValidHostname;
  } catch {
    return false;
  }
};

export default function OrgGeneralSettingsEditor(props: OrgGeneralSettingsEditorProps): ReactElement {
  const { org, isNew, onUpdate, onImageUpdated, onFileSelected, onRequestVanityUrl, onCancelVanityUrl } = props;

  const urlError = useMemo(() => {
    if (!org.homeUrl) return null;
    return isValidUrl(org.homeUrl) ? null : 'Enter a valid URL';
  }, [org.homeUrl]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{
        alignItems: "center"
      }}>
        <TextField
          id="name"
          label="Name"
          aria-label="Org Name"
          aria-required="true"
          required
          autoComplete="off"
          slotProps={{ htmlInput: { 'data-1p-ignore': true } }}
          value={org.name}
          onChange={({ target }) => onUpdate('name', target.value)}
          sx={{ flexGrow: 1, maxWidth: 'sm' }}
        />
        <GratiImageUpload
          onImageUpdated={isNew ? undefined : onImageUpdated}
          onFileSelected={isNew ? onFileSelected : undefined}
          deferUpload={isNew}
          isInitialized={!isNew && Boolean(org.logo)}
          storageImageFileName={org.itemId + '-logo'}
          storageImageLocation={`org/${org.itemId}/publicFile`}
          label="Logo"
          isCompact
          size="large"
        />
      </Stack>
      <TextField
        id="shortName"
        label="Short name"
        aria-label="Short name"
        aria-required="true"
        required
        value={org.shortName}
        onChange={({ target }) => onUpdate('shortName', target.value)}
        sx={{ maxWidth: '18.75rem' }}
      />
      <TextField
        id="homeUrl"
        label="Website"
        aria-label="Website"
        aria-required="false"
        type="url"
        placeholder="example.com"
        value={org.homeUrl}
        onChange={({ target }) => onUpdate('homeUrl', target.value)}
        error={!!urlError}
        helperText={urlError}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <LanguageIcon color={urlError ? 'error' : 'action'} />
              </InputAdornment>
            ),
          },
        }}
        sx={{ maxWidth: 'sm' }}
      />
      <TextField
        id="description"
        label="Description"
        aria-label="Description"
        aria-required="false"
        multiline
        minRows={2}
        value={org.description}
        onChange={({ target }) => onUpdate('description', target.value)}
        sx={{ maxWidth: 'sm' }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={org.defaultIsTeam ?? false}
            onChange={(e) => onUpdate('defaultIsTeam', e.target.checked)}
          />
        }
        label="New leagues default to team leagues"
      />
      {org.defaultIsTeam && (
        <FormControl size="small" sx={{ maxWidth: '10em' }}>
          <FormLabel component="legend" >Default Team Size</FormLabel>
          <Select
            value={org.defaultTeamSize ?? 4}
            onChange={(e) => onUpdate('defaultTeamSize', Number(e.target.value))}
          >
            {Array.from({ length: 19 }, (_, i) => i + 2).map((size) => (
              <MenuItem key={size} value={size}>{size} players</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {!isNew && (
        <FormControlLabel
          control={
            <Checkbox
              checked={org.notifications?.includes(ORG_NOTIFICATION_TYPES.TEAM_INVITE_REQUEST) ?? false}
              onChange={(e) => {
                const current = org.notifications ?? [];
                const updated: OrgNotificationType[] = e.target.checked
                  ? [...current, ORG_NOTIFICATION_TYPES.TEAM_INVITE_REQUEST]
                  : current.filter((n) => n !== ORG_NOTIFICATION_TYPES.TEAM_INVITE_REQUEST);
                onUpdate('notifications', updated);
              }}
            />
          }
          label="Notify admins when team managers request new members"
        />
      )}
      {!isNew && onRequestVanityUrl && (
        <OrgPublicPageEditor org={org} onRequestVanityUrl={onRequestVanityUrl} onCancelVanityUrl={onCancelVanityUrl} />
      )}
    </Stack>
  );
}
